import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { ITemplate } from '@feathr/blackbox';
import type { Template } from '@feathr/blackbox';
import { ArchiveModal, ConfirmModal, ContextMenu, EAlertV2Type, toast } from '@feathr/components';
import TemplateSummary from '@feathr/extender/components/TemplateSummary';
import { useLocalUrl, useStore, useUser } from '@feathr/extender/state';
import { getIconForAction, sanitizeFileName, useToggle } from '@feathr/hooks';

interface IProps {
  template: Template;
}

function AccountTemplatesOptions({ template }: IProps): JSX.Element {
  const { Templates } = useStore();
  const localUrl = useLocalUrl();
  const [isArchiveModalOpen, toggleArchiveModalOpen] = useToggle(false);
  const [isDuplicateModalOpen, toggleDuplicateModalOpen] = useToggle(false);
  const [isDefaultModalOpen, toggleDefaultModalOpen] = useToggle(false);
  const { t } = useTranslation();
  const user = useUser();

  const isDefault = !!template.get('default');
  const isReadOnly = !!template.isReadOnly;
  const isFeathrSuperuser = !!user?.isSuperuser;

  async function handleDuplicate(): Promise<void> {
    const patch = {
      default: false,
      parent_kind: 'account',
      parent: template.get('account'),
    } as Partial<ITemplate>;

    const updatedTemplate = await template.clone(patch);
    Templates.refreshApiCache();

    if (updatedTemplate.error) {
      toast(
        t('An error occurred while trying to duplicate your template:\n{{error}}', {
          error: updatedTemplate.error.message || 'Unexpected error',
        }),
        { type: 'error' },
      );
    } else {
      toast(t('Your template has been duplicated.'), { type: 'success' });
    }

    toggleDuplicateModalOpen();
  }

  async function handleMakeDefault(): Promise<void> {
    const patch = {
      default: true,
      parent: undefined,
      parent_kind: undefined,
    } as Partial<ITemplate>;

    const updatedTemplate = await template.patch(patch);

    if (updatedTemplate.error) {
      toast(
        t('An error occurred while trying to make your template a default template:\n{{error}}', {
          error: updatedTemplate.error.message || 'Unexpected error',
        }),
        { type: 'error' },
      );
    } else {
      toast(t('Your template has been made a default template.'), { type: 'success' });
    }

    toggleDefaultModalOpen();
  }

  function save(filename: string, content: string): void {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  function exportHTML(): void {
    const content = template.get('content')['_html'];
    const contentString = typeof content === 'string' ? content : JSON.stringify(content);
    const { name: templateName } = template;
    const sanitizedFileName = sanitizeFileName(templateName) + '.html';
    try {
      save(sanitizedFileName, contentString);
      toast(
        t('"{{templateName}}" template successfully exported as HTML.', {
          templateName,
        }),
        { type: 'success' },
      );
    } catch (error) {
      toast(
        t(
          'There was an issue exporting the "{{templateName}}" template as HTML. Please try again later. \n{{- error}}',
          {
            templateName,
            error,
          },
        ),
        { type: 'error' },
      );
    }
  }

  return (
    <>
      <ContextMenu buttonType={'icon'} position={'left-start'}>
        <ContextMenu.Item
          disabled={template.isUpdating}
          link={localUrl(template.getItemUrl())}
          prefix={getIconForAction('view')}
        >
          {t('View')}
        </ContextMenu.Item>
        <ContextMenu.Item
          disabled={template.isUpdating || isDefault || isReadOnly}
          link={localUrl(template.getItemUrl('edit'))}
          prefix={getIconForAction('edit')}
        >
          {t('Edit')}
        </ContextMenu.Item>
        <ContextMenu.Item
          disabled={template.isUpdating || isReadOnly}
          onClick={exportHTML}
          prefix={getIconForAction('export')}
        >
          {t('Export as HTML')}
        </ContextMenu.Item>
        <ContextMenu.Divider />
        <ContextMenu.Item
          disabled={template.isUpdating}
          onClick={toggleDuplicateModalOpen}
          prefix={getIconForAction('duplicate')}
        >
          {t('Duplicate')}
        </ContextMenu.Item>
        {!isDefault && isFeathrSuperuser && (
          <ContextMenu.Item
            disabled={template.isUpdating}
            onClick={toggleDefaultModalOpen}
            prefix={getIconForAction('template')}
          >
            {t('Make default template')}
          </ContextMenu.Item>
        )}
        <ContextMenu.Divider />
        <ContextMenu.Item
          disabled={template.isUpdating || isDefault}
          onClick={toggleArchiveModalOpen}
          prefix={getIconForAction('archive')}
          theme={'danger'}
        >
          {t('Archive')}
        </ContextMenu.Item>
      </ContextMenu>
      {isDuplicateModalOpen && (
        <ConfirmModal
          alertTitle={t('Are you sure you want to duplicate this template?')}
          onClose={toggleDuplicateModalOpen}
          onConfirm={handleDuplicate}
          opened={isDuplicateModalOpen}
          t={t}
          title={t('Duplicate')}
        >
          <TemplateSummary template={template} />
        </ConfirmModal>
      )}
      {isDefaultModalOpen && (
        <ConfirmModal
          alertTitle={t(
            'Are you sure you want to make this a default template? This will make it available to all accounts!',
          )}
          alertType={EAlertV2Type.warning}
          onClose={toggleDefaultModalOpen}
          onConfirm={handleMakeDefault}
          opened={isDefaultModalOpen}
          t={t}
          title={t('Make Default Template')}
        >
          <TemplateSummary template={template} />
        </ConfirmModal>
      )}
      {isArchiveModalOpen && (
        <ArchiveModal
          errorMessage={t('There was an issue archiving the template. Please try again later.')}
          model={template}
          onClose={toggleArchiveModalOpen}
          opened={isArchiveModalOpen}
          successMessage={t('Your template {{name}} has been archived.', { name: template.name })}
          t={t}
        >
          <TemplateSummary template={template} />
        </ArchiveModal>
      )}
    </>
  );
}

export default observer(AccountTemplatesOptions);
